import { useAuthStore } from '@/entities/session/auth-store';
import * as authApi from '@/entities/session/auth-api';
import { useThemeStore } from '@kidshome/ui';
import { personaFor } from '@/entities/session/persona';
import i18n, { setLocale } from '@/app/i18n';

/**
 * Resolve the current session for the RR7 root loader. On a cold load there is no in-memory access
 * token, so we try a transparent refresh (uses the httpOnly refresh cookie); if that yields a token
 * we fetch `me`. Returns the user or null. Also applies the persona default theme (unless the user
 * already chose one) and mirrors `User.locale` to i18n.
 *
 * Idempotent + cached: once resolved we don't re-run the refresh probe on every navigation.
 */
let bootstrapped: Promise<void> | null = null;

// An explicit-logout "poison" marker (survives a cold reload). The refresh cookie is httpOnly and
// JS-unreachable, so a logout whose server call failed (offline / hiccup) leaves it valid — without this
// marker the next cold load would silently refresh-restore the guardian on a shared tablet. Set in
// logout(), cleared on any interactive login.
const LOGGED_OUT_KEY = 'kidshome.loggedOut';

export function markLoggedOut(): void {
  try {
    window.localStorage.setItem(LOGGED_OUT_KEY, '1');
  } catch {
    // Storage unavailable (private mode / quota) — best-effort only.
  }
}

export function clearLoggedOutMarker(): void {
  try {
    window.localStorage.removeItem(LOGGED_OUT_KEY);
  } catch {
    // ignore
  }
}

function wasLoggedOut(): boolean {
  try {
    return window.localStorage.getItem(LOGGED_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

async function probeSession(): Promise<void> {
  const store = useAuthStore.getState();
  if (store.accessToken && store.user) {
    return;
  }
  // The user explicitly logged out and no interactive login has happened since — do NOT silently restore
  // the session from a refresh cookie that a failed logout call may have left valid (shared-tablet safety).
  if (wasLoggedOut()) {
    store.clear();
    return;
  }
  try {
    // No access token yet — attempt to restore from the refresh cookie. `/refresh` is CSRF-protected,
    // so fetch a CSRF token first; it is signed against a stable identifier (not the rotating `rt`
    // cookie), so it stays valid across the rotation — no post-refresh re-fetch needed.
    const { csrfToken } = await authApi.fetchCsrfToken();
    store.setCsrfToken(csrfToken);
    const tokens = await authApi.refresh();
    store.setTokens(tokens);
    const me = await authApi.fetchMe();
    store.setUser(me);
    applyPersonaAndLocale();
  } catch {
    store.clear();
  }
}

function applyPersonaAndLocale(): void {
  const me = useAuthStore.getState().user;
  if (!me) {
    return;
  }
  // Persist the persona's default theme so the pre-paint script renders it flash-free next cold load:
  // a child → cozy-pastel, a parent/relative → warm-neutral-teal. (Admin users are the family persona
  // here; the admin/platform shells scope command-center themselves while mounted.)
  useThemeStore.getState().applyPersonaDefault(personaFor(me));
  if (me.locale && me.locale !== i18n.resolvedLanguage) {
    setLocale(me.locale);
  }
}

export async function ensureSession(): Promise<ReturnType<typeof useAuthStore.getState>['user']> {
  bootstrapped ??= probeSession();
  await bootstrapped;
  return useAuthStore.getState().user;
}

/** After an explicit login the store already has the session; sync persona theme + locale. */
export function onLoggedIn(): void {
  applyPersonaAndLocale();
}

/** Reset the bootstrap cache (after logout) so the next load re-probes. */
export function resetBootstrap(): void {
  bootstrapped = null;
}
